import React from 'react';
import AdminLayout from '../../components/AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faChartLine, faDollarSign, faStore, faUsers } from '@fortawesome/free-solid-svg-icons';
import { AdminStatisticsDto, fetchAdminStatistics } from '@/features/adminPlatform/api';

const formatAmt = (n: number) =>
  new Intl.NumberFormat('cs-CZ', { style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }).format(n);

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = React.useState<AdminStatisticsDto | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    fetchAdminStatistics()
      .then(setStats)
      .catch(err => setError(err?.message ?? 'Nelze načíst statistiky'))
      .finally(() => setLoading(false));
  }, []);

  const cards = stats
    ? [
        { title: 'Celkem uživatelů', value: stats.totalUsers.toLocaleString('cs-CZ'), change: `+${stats.newUsersLast30Days} za 30 dní`, icon: faUsers, color: 'bg-blue-500' },
        { title: 'Rezervací celkem', value: stats.totalBookings.toLocaleString('cs-CZ'), change: `+${stats.bookingsLast30Days} za 30 dní`, icon: faCalendarCheck, color: 'bg-green-500' },
        { title: 'Aktivní salony', value: stats.totalSalons.toLocaleString('cs-CZ'), change: '', icon: faStore, color: 'bg-purple-500' },
        { title: 'Celkové tržby', value: formatAmt(stats.totalRevenue), change: '', icon: faDollarSign, color: 'bg-yellow-500' },
      ]
    : [];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">{error}</div>
        )}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-sm text-gray-500">Načítám…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {cards.map((c, i) => (
                <div key={i} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{c.title}</p>
                      <p className="text-2xl font-bold text-gray-900">{c.value}</p>
                      {c.change && <p className="text-sm text-green-600">{c.change}</p>}
                    </div>
                    <div className={`p-3 rounded-full ${c.color}`}>
                      <FontAwesomeIcon icon={c.icon} className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Top salony</h3>
                <FontAwesomeIcon icon={faChartLine} className="text-gray-400" />
              </div>
              {stats && stats.topSalons.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salon</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rezervací</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tržby</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {stats.topSalons.map(s => (
                      <tr key={s.salonId}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.salonName}</td>
                        <td className="px-4 py-3 text-sm">{s.bookingCount.toLocaleString('cs-CZ')}</td>
                        <td className="px-4 py-3 text-sm">{formatAmt(s.totalRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="p-6 text-sm text-gray-500">Žádná data.</div>
              )}
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
